import { useState, KeyboardEvent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Checkbox, Message } from '@la/ds-ui-components';
import { ESignature } from 'components/ESignature/ESignature';
import { useConsolidatedCheckout } from 'lib/context/ConsolidatedCheckoutContext/ConsolidatedCheckoutContext';
import { CHECKOUT_ERRORS } from '../CheckoutErrors';
import PaymentPolicyModal from '../PaymentPolicyModal/PaymentPolicyModal';
import styles from './TermsAndConditionsAgreementBox.module.scss';

export type TermsAndConditionsAgreementBoxProps = {
  policyContent?: string;
  policyName?: string;
};

/* TermsAndConditionsAgreementBox */
export default function TermsAndConditionsAgreementBox({
  policyContent,
  policyName,
}: TermsAndConditionsAgreementBoxProps) {
  const {
    checkoutErrorsAreVisible,
    checkoutFieldErrorStates,
    eSignature,
    handleESignatureUpdate,
    handleHasAgreedToPaymentPolicy,
    hasAgreedToPaymentPolicy,
  } = useConsolidatedCheckout();
  const { ngESignature } = useFlags();

  const [showESignature, setShowESignature] = useState<boolean>(false);
  const [isOpenPaymentPolicyModal, setIsOpenPaymentPolicyModal] =
    useState<boolean>(false);

  function openPolicyModal() {
    setIsOpenPaymentPolicyModal(true);
  }

  function closePolicyModal() {
    setIsOpenPaymentPolicyModal(false);
  }

  function handleKeyDownTermsModal(
    evt: KeyboardEvent<HTMLButtonElement>
  ): void {
    if (evt.key === 'Enter' || evt.key === ' ') {
      evt.preventDefault();
      openPolicyModal();
    }
  }

  const { hasESignatureError, hasPaymentPolicyError } =
    checkoutFieldErrorStates;
  let errorMessage;
  if (
    (hasESignatureError || hasPaymentPolicyError) &&
    checkoutErrorsAreVisible
  ) {
    if (hasPaymentPolicyError && !hasAgreedToPaymentPolicy) {
      errorMessage = CHECKOUT_ERRORS.paymentTerms;
    } else if (hasESignatureError) {
      errorMessage = CHECKOUT_ERRORS.eSignatureError;
    }
  }

  return (
    <>
      <div className={styles.termsAndConditionsAgreementBoxContainer}>
        <div className={styles.termsAndConditionsAgreementBox}>
          <Checkbox
            ariaLabel={`I have read and agree to: ${policyName}`}
            checked={hasAgreedToPaymentPolicy}
            id="terms-condition-agreement"
            onCheckedChange={(checked) => {
              handleHasAgreedToPaymentPolicy(checked);
              setShowESignature(checked);
            }}
            size="large"
          />
          <div className={styles.label}>
            I have read and agree to:{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={openPolicyModal}
              onKeyDown={handleKeyDownTermsModal}
            >
              {policyName || 'Payment and Refund Policies'}
            </span>
          </div>
        </div>
        {ngESignature && showESignature ? (
          <ESignature
            eSignature={eSignature}
            hasError={checkoutFieldErrorStates.hasESignatureError}
            onChange={handleESignatureUpdate}
            showError={false}
          />
        ) : null}
        {errorMessage ? (
          <Message messageType="error">{errorMessage}</Message>
        ) : null}
      </div>
      <PaymentPolicyModal
        closeModal={closePolicyModal}
        modalTitle="Payment policies"
        open={isOpenPaymentPolicyModal}
        policyContent={policyContent}
        policyName={policyName}
      />
    </>
  );
}
/* */
